import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useStore, useTranslation } from '../store/useStore';
import { Shield, Eye, EyeOff, Mail, Lock, User, Phone, MapPin } from 'lucide-react';

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAuth } = useStore();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'buyer' as 'buyer' | 'supplier' | 'member',
    countryCode: 'US',
    city: '',
    agreeTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (field: string, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!form.agreeTerms) {
      setError('You must agree to the Terms of Service');
      return;
    }

    setLoading(true);

    setTimeout(() => {
      const newId = 100 + Math.floor(Math.random() * 1000);
      setAuth(
        {
          id: newId,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          role: form.role,
          status: 'active',
          kycStatus: 'unverified',
          preferredLanguage: 'en',
          preferredCurrency: 'USD',
          countryCode: form.countryCode,
          city: form.city,
          foundingMember: false,
          ipmeStatus: 'none',
        },
        'demo_token_' + newId
      );
      navigate('/');
      setLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] via-[#e0e7ff]/20 to-[#fed7aa]/20 py-24">
      <div className="w-full max-w-lg px-4">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 bg-gradient-hero rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-semibold text-[#1e293b] font-display">{t('auth.register')}</h1>
            <p className="text-sm text-[#64748b] mt-1">Join the world's most secure marketplace</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-[#1e293b] mb-2">{t('auth.role')}</label>
              <div className="grid grid-cols-3 gap-2">
                {(['buyer', 'supplier', 'member'] as const).map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => handleChange('role', role)}
                    className={`py-2.5 px-3 rounded-xl border text-sm font-medium capitalize transition-colors ${form.role === role ? 'border-[#c2410c] bg-[#c2410c]/5 text-[#c2410c]' : 'border-gray-200 text-[#64748b] hover:border-[#c2410c]/30'}`}
                  >
                    {role === 'member' ? 'Affiliate' : role}
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[#1e293b] mb-1.5">{t('auth.first_name')}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    placeholder="John"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1e293b] mb-1.5">{t('auth.last_name')}</label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Doe"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[#1e293b] mb-1.5">{t('auth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
                  required
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-[#1e293b] mb-1.5">{t('auth.phone')}</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+1 234 567 890"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
                />
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Country</label>
                <select
                  value={form.countryCode}
                  onChange={(e) => handleChange('countryCode', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] outline-none text-sm"
                >
                  <option value="US">United States</option>
                  <option value="SG">Singapore</option>
                  <option value="GB">United Kingdom</option>
                  <option value="FR">France</option>
                  <option value="DE">Germany</option>
                  <option value="ES">Spain</option>
                  <option value="AE">UAE</option>
                  <option value="KE">Kenya</option>
                  <option value="BR">Brazil</option>
                  <option value="CN">China</option>
                  <option value="IN">India</option>
                  <option value="JP">Japan</option>
                  <option value="GH">Ghana</option>
                  <option value="NG">Nigeria</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1e293b] mb-1.5">City</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="New York"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#1e293b] mb-1.5">{t('auth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Min. 8 characters"
                  className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Confirm Password</label>
              <input
                type="password"
                value={form.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder="Repeat password"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
                required
              />
            </div>

            {/* Terms */}
            <label className="flex items-start gap-2 text-sm text-[#64748b]">
              <input
                type="checkbox"
                checked={form.agreeTerms}
                onChange={(e) => handleChange('agreeTerms', e.target.checked)}
                className="mt-0.5 rounded border-gray-300"
              />
              <span>I agree to the <Link to="#" className="text-[#c2410c] hover:underline">Terms of Service</Link> and <Link to="#" className="text-[#c2410c] hover:underline">Privacy Policy</Link></span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold disabled:opacity-50"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-[#64748b]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#c2410c] font-medium hover:underline">{t('auth.login_now')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
